/**
 * forgot-password.tsx — Forgot-Password-Route (W0.4)
 *
 * Sendet Reset-Link via Api::V2::PasswordsController#create (AUTH-Block-Fix:
 * vormals POST an /api/v2/users/password — eine Route, die nie existierte).
 * Bei echtem 200: neutrale Erfolgsmeldung (kein User-Enumeration — Backend
 * antwortet immer mit 200, unabhaengig davon ob die Email existiert).
 * Bei echten Server-/Netzfehlern (res.ok === false oder Exception) wird ein
 * Fehler angezeigt statt der Erfolgsmeldung (AUTH-Block-Fix).
 */
import { createFileRoute } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

function buildForgotSchema(t: (key: string) => string) {
  return z.object({
    email: z.string().email(t('validation.invalidEmail')),
  })
}

type ForgotInput = z.infer<ReturnType<typeof buildForgotSchema>>

const PASSWORD_RESET_URL = `${import.meta.env.VITE_API_BASE_URL}/api/v2/auth/password`

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
})

function ForgotPasswordPage() {
  const { t } = useTranslation('auth')
  const [submitted, setSubmitted] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  const forgotSchema = useMemo(() => buildForgotSchema(t), [t])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotInput>({
    resolver: zodResolver(forgotSchema),
  })

  async function onSubmit(data: ForgotInput) {
    setServerError(null)
    try {
      const res = await fetch(PASSWORD_RESET_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email }),
      })

      if (!res.ok) {
        // Echter Server-Fehler (5xx/4xx) — Anti-Enumeration betrifft nur die
        // Existenz der Email, nicht generelle Serverausfaelle.
        setServerError(t('forgotPassword.errors.serverError'))
        return
      }
    } catch {
      setServerError(t('forgotPassword.errors.networkError'))
      return
    }
    // Immer Erfolgsmeldung zeigen (AC-5 Security) — Backend liefert bei
    // 200 keine Information darueber, ob die Email tatsaechlich existiert.
    setSubmitted(true)
  }

  return (
    <div className="auth-shell" data-testid="forgot-password-page">
      {/* Linke Spalte: Formular */}
      <div className="auth-form-wrap">
        <div className="auth-form">
          {/* Logo-Mark */}
          <div className="row" style={{ gap: 10 }}>
            <div className="logo-mark">H</div>
            <div style={{ fontWeight: 600 }}>HeliumTracker</div>
          </div>

          <div>
            <h1 style={{ fontSize: 28 }}>{t('forgotPassword.title')}</h1>
            <p className="muted" style={{ margin: '6px 0 0' }}>
              {t('forgotPassword.subtitle')}
            </p>
          </div>

          {serverError && (
            <div
              role="alert"
              aria-live="polite"
              data-testid="form-error"
              className="alert alert-error"
            >
              {serverError}
            </div>
          )}

          {submitted ? (
            <div
              role="alert"
              aria-live="polite"
              data-testid="success-message"
              className="alert alert-success"
            >
              {t('forgotPassword.successMessage')}
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} noValidate aria-label={t('forgotPassword.formAriaLabel')} style={{ display: 'contents' }}>
              <div>
                <label className="label" htmlFor="fp-email">{t('forgotPassword.emailLabel')}</label>
                <input
                  id="fp-email"
                  type="email"
                  className="input"
                  aria-label={t('forgotPassword.emailAriaLabel')}
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'fp-email-error' : undefined}
                  {...register('email')}
                />
                {errors.email && (
                  <span
                    id="fp-email-error"
                    role="alert"
                    aria-live="polite"
                    className="field-error"
                  >
                    {errors.email.message}
                  </span>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-lg"
                disabled={isSubmitting}
                aria-label={t('forgotPassword.submitAriaLabel')}
                style={{ width: '100%' }}
                data-testid="submit-button"
              >
                {isSubmitting ? t('forgotPassword.submitting') : t('forgotPassword.submitLabel')}
              </button>
            </form>
          )}

          <div className="muted" style={{ fontSize: 13, textAlign: 'center' }}>
            <a href="/login" style={{ color: 'var(--primary)' }}>
              {t('forgotPassword.backToLogin')}
            </a>
          </div>
        </div>
      </div>

      {/* Rechte Spalte: Branding-Aside */}
      <aside className="auth-aside">
        <div className="row" style={{ gap: 10 }}>
          <div className="logo-mark">H</div>
          <div style={{ fontWeight: 600 }}>HeliumTracker</div>
        </div>

        <div>
          <h2 style={{ fontSize: 30, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
            {t('forgotPassword.aside.headline')}
          </h2>
          <p className="muted" style={{ marginTop: 14, fontSize: 14, maxWidth: 480, lineHeight: 1.7 }}>
            {t('forgotPassword.aside.description')}
          </p>
        </div>

        <div className="muted" style={{ fontSize: 11 }}>
          {t('aside.copyright')}
        </div>
      </aside>
    </div>
  )
}
